import { useTranslation } from "react-i18next";
import { QRCode } from "antd";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import CollapsibleText from "@components/CommonStyles/CollapsibleText";
import TagSpecialPackageDetailType from "@components/CommonStyles/TagSpecialPackageDetailType";
import VoucherCode from "./VoucherCode";
import { ETagSpecialPackageDetailType } from "@base/enums";
import {
  actionKind,
  claimedVoucher,
  EffectiveVisitFn,
  isEVoucher,
  PackageRoute,
  toVnDmy,
  parseVnDate,
  visitButton,
  visitStatusView,
  VisitStatusView,
} from "@base/modules/patientSpecialPackage/packageDetailRules";
import {
  ISpecialPackage,
  ISpecialPackageDetail,
  ISpecialPackageDetailImplement,
} from "@base/modules/patientSpecialPackage/patientSpecialPackage.interface";

interface IProps {
  open: boolean;
  toggle: () => void;
  route: PackageRoute;
  specialPackage: ISpecialPackage;
  service: ISpecialPackageDetail;
  eff: EffectiveVisitFn;
  actionLabel: string;
  bookLabel: string;
  onAction: () => void;
  onBook: (visit: ISpecialPackageDetailImplement) => void;
  onDetail: (apptKey: string) => void;
}

// Shaped and coloured like the package's own status tag (TagSpecialPackageStatus).
const STATUS_CLASS: Record<VisitStatusView, string> = {
  COMPLETED: "bg-gray-100 text-gray-500",
  IN_PROGRESS: "bg-common-yellow100 text-common-yellow600",
  CANCELLED: "bg-common-red100 text-common-red500",
  BOOKED: "bg-common-yellow100 text-common-yellow600",
  NOT_DONE: "bg-common-purple100 text-common-purple500",
};

const formatDate = (value?: unknown) => {
  const parsed = parseVnDate(value);

  return parsed ? toVnDmy(parsed) : "";
};

/** Every use of one service, each with what can be done with it (§11). */
export default function VisitHistoryModal(props: IProps) {
  //! state
  const { open, toggle, route, specialPackage, service, eff, actionLabel, bookLabel, onAction, onBook, onDetail } = props;
  const { t } = useTranslation();
  const visits = [...(service.patient_special_package_detail_implements ?? [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  const voucher = isEVoucher(service) ? claimedVoucher(service) : null;
  const action = actionKind(service);

  //! render
  const statusLabel = (status: VisitStatusView) => {
    if (status === "BOOKED") {
      return route === "GROUP_EXAM" ? t("booking.visit.bookingInProgress") : t("booking.visit.hasAppointment");
    }

    return t(`booking.visit.${status}`);
  };

  const statusClass = (status: VisitStatusView) =>
    status === "BOOKED" && route === "GROUP_EXAM" ? "bg-common-blue100 text-common-blue500" : STATUS_CLASS[status];

  const renderVisit = (visit: ISpecialPackageDetailImplement, index: number) => {
    const status = visitStatusView(visit, eff);
    const button = visitButton(specialPackage, service, visit, eff);
    const dateLine =
      status === "COMPLETED" && visit.implemented_date
        ? t("packageBooking.completedOn", { date: formatDate(visit.implemented_date) })
        : visit.expected_date
          ? t("packageBooking.expectedOn", { date: formatDate(visit.expected_date) })
          : "";

    return (
      <div
        key={visit.id ?? index}
        className="bg-common-background rounded-lg p-3 flex justify-between items-center max-sm:flex-col max-sm:items-start gap-4 max-sm:gap-2"
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-common-maintext text-sm font-medium font-['Inter']">
              {t("packageBooking.implementSequence", { sequence: index + 1 })}
            </span>
            <CommonStyles.TagUI
              className={`text-xs font-medium border-none p-1 rounded !m-0 ${statusClass(status)}`}
              tagName={statusLabel(status)}
            />
          </div>

          {dateLine ? <span className="text-common-subtext text-xs font-normal font-['Inter']">{dateLine}</span> : null}
          {visit.voucher?.trim() ? <VoucherCode code={visit.voucher.trim()} /> : null}
        </div>

        <div className="max-sm:w-full flex justify-end">
          {button === "ACTION" ? <CommonStyles.ButtonUI onClick={onAction}>{actionLabel}</CommonStyles.ButtonUI> : null}
          {button === "BOOK" ? <CommonStyles.ButtonUI onClick={() => onBook(visit)}>{bookLabel}</CommonStyles.ButtonUI> : null}
          {button === "DETAIL" && visit.appt_key ? (
            <CommonStyles.ButtonUI ghost onClick={() => onDetail(String(visit.appt_key))}>
              {t("user.servicePackage.btnApptDetail")}
            </CommonStyles.ButtonUI>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      hiddenAction
      width={640}
      title={<span className="block pr-6">{service.item_name}</span>}
      content={
        <div className="flex flex-col gap-3">
          {service.description ? (
            <div className="flex flex-col gap-1">
              <span className="text-common-maintext text-sm font-semibold font-['Inter']">{t("booking.packageInfo.description")}</span>
              <CollapsibleText text={service.description} />
            </div>
          ) : null}

          {!isEVoucher(service) ? (
            <div className="flex items-center gap-2">
              <span className="text-common-subtext text-sm font-normal font-['Inter']">{t("booking.visit.channel")}</span>
              <TagSpecialPackageDetailType type={service.type as ETagSpecialPackageDetailType} />
            </div>
          ) : null}

          {voucher ? (
            <div className="flex flex-col items-center gap-3 p-4 rounded-lg bg-common-background">
              <span className="text-common-maintext text-base font-semibold font-['Inter']">{t("booking.visit.voucherCode", { code: voucher })}</span>
              <QRCode value={voucher} size={160} bordered={false} />
              <CommonStyles.ButtonUI
                ghost
                icon={<CommonIcons.IconCopy />}
                onClick={() => {
                  navigator.clipboard?.writeText(voucher);
                }}
              >
                {t("copy")}
              </CommonStyles.ButtonUI>
              {action.kind === "CALL" ? <span className="text-common-subtext text-xs font-['Inter']">{actionLabel}</span> : null}
            </div>
          ) : (
            <div className="flex flex-col gap-2 max-h-[calc(100vh-320px)] overflow-y-auto">
              {visits.map(renderVisit)}
              {!visits.length ? (
                <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
                  {t("booking.visit.empty")}
                </div>
              ) : null}
            </div>
          )}
        </div>
      }
    />
  );
}
